import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

export const Tile = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 180ms ease;
  cursor: pointer;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &:focus-visible {
    outline: 0;
  }
`

/** On mobile the ticket/booked labels drop to their own row underneath the flight
 * summary, so the route and times keep the full tile width. Grid areas (rather than
 * duplicated markup) let the labels move without changing the DOM. */
export const HeaderRow = styled.div`
  display: grid;
  align-items: center;
  column-gap: ${rem(12)};
  row-gap: ${rem(8)};
  padding: ${rem(14)} ${rem(16)};
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  grid-template-areas:
    "chevron logo summary menu"
    "chevron .    labels  .";

  ${mediaQueryUp.tablet} {
    grid-template-columns: auto auto minmax(0, 1fr) auto auto;
    grid-template-areas: "chevron logo summary labels menu";
  }
`

export const HeaderLogo = styled.div`
  grid-area: logo;
  display: inline-flex;
`

export const HeaderChevron = styled.div<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.default.two};
  transform: rotate(${props => (props.$expanded ? '0deg' : '-90deg')});
  transition: transform 200ms ease;
`

export const CarrierLogoFrame = styled.div<{ $size: number; $bordered?: boolean }>`
  width: ${props => rem(props.$size)};
  height: ${props => rem(props.$size)};
  border-radius: ${rem(6)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: ${props => (props.$bordered === false ? 'none' : `1px solid ${props.theme.palette.neutral.surface.lightForeground}`)};
  background: ${props => (props.$bordered === false ? 'transparent' : props.theme.palette.neutral.surface.normalBackground)};
`

export const CarrierLogoImage = styled.img<{ $size: number }>`
  width: ${props => rem(props.$size)};
  height: ${props => rem(props.$size)};
  object-fit: contain;
`

export const HeaderText = styled.div`
  display: grid;
  gap: ${rem(2)};
  min-width: 0;
`

export const HeaderSummary = styled(HeaderText)`
  grid-area: summary;
`

export const HeaderLabels = styled.div`
  grid-area: labels;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex-wrap: wrap;

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

export const HeaderTrailing = styled.div`
  grid-area: menu;
  display: flex;
  align-items: center;
`

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

export const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;

  & + &::before {
    content: '·';
    margin: 0 ${rem(6)};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

export const ChevronButton = styled.button`
  grid-area: chevron;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 0;
  }
`

export const ExpandedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 ${rem(16)} ${rem(16)};
`

export const LegBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
`

export const LegCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)} ${rem(16)};
  transition: border-color 180ms ease;
`

export const LegPlainRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(12)} ${rem(16)};
`

export const SavedOptionGroupCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  transition: border-color 180ms ease;
`

export const LegRoute = styled.div`
  flex-shrink: 0;
  min-width: ${rem(64)};
`

export const LegDateTime = styled.div`
  display: grid;
  gap: ${rem(2)};
  min-width: 0;
`

export const LegFlightNumber = styled.div`
  margin-inline-start: auto;
  text-align: end;
  flex-shrink: 0;
`

export const LayoverRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  padding-inline-start: ${rem(2)};
  color: ${props => props.theme.palette.neutral.default.two};

  svg {
    width: ${rem(14)};
    height: ${rem(14)};
    flex-shrink: 0;
  }
`

export const TileFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const ViewBookingFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

export const SavedOptionsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  padding: ${rem(8)} 0 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.default.one};
  cursor: pointer;

  &:focus-visible {
    outline: 0;
  }
`

export const SavedOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding-top: ${rem(8)};
`

export const SavedOptionRow = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
  text-align: start;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 0;
  }

  &:hover ${LegCard},
  &:hover ${SavedOptionGroupCard} {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`
